import React, {Component} from 'react';
import moment from "moment";
import ValidationUtil from "../../../../components/form/validator/ValidationUtil";
import SignatureInput from "../../../../components/signature-input/SignatureInput";
import {pick} from "lodash-es";
import {TextInput, Wizard} from "../../../../components";
import SelectInput from "../../../../components/select-input/SelectInput";
import {FormField} from "../../../../components/form";
import PropTypes from "prop-types";
import RemotingService from '../../../../services/remoting-service/RemotingService';
import PatientUtil from '../../../../utils/PatientUtil';
import StringUtil from '../../../../utils/StringUtil';
import EnumAppointmentType from '../../../appointment/enums/EnumAppointmentType';
import DateUtil from "../../../../utils/DateUtil";
import EnumInstallation, {DEFAULT_INSTALLATION} from "../../../../utils/EnumInstallation";
import ClinicLogo from "../../../../layout/ClinicLogo";

class PackageConsentForm extends Component {

    static propTypes = {
        show: PropTypes.func,
        close: PropTypes.func,
        onFormComplete: PropTypes.func,
        form: PropTypes.object,
        language: PropTypes.string,
        readOnly: PropTypes.bool
    }

    constructor(props) {
        super(props)
        const {form} = this.props;
        this.state = {
            consentDate: form?.consentDate ? new Date(form.consentDate) : new Date(),
            patientSignature: form ? form.patientSignature : null,
            patientName: '',
            mrnNo: '',
            mobileNo: '',
            email: '',
            packageDetails: '',
            endDate: '',
            sessionSignatures: form?.sessionSignatures || [],
            usedSessionCount: 0,
            completedAppointments: []
        };
    }

    populateFromEnhancedForm(form) {
        const {patientName} = this.state;

        this.setState({
            patientName: patientName || form.patientName,
            mrnNo: form.mrnNo,
            mobileNo: form.mobileNo,
            email: form.email
        });

        const allAppointments = form.completedAppointments || [];
        this.setState({
            completedAppointments: allAppointments
        }, () => {
            this.populateExistingSessionsWithAppointmentData();
        });

        const sessionCount = this.extractSessionCountFromPackageDetails(form.packageDetails);
        const existingSignatures = form?.sessionSignatures || [];
        const initialSessionSignatures = Array(sessionCount).fill(null).map((_, index) => {
            return existingSignatures[index] || {
                sessionNumber: index + 1,
                date: '',
                therapist: '',
                patientSignature: ''
            };
        });

        this.setState({
            packageDetails: form.packageDetails,
            endDate: form.endDate,
            sessionSignatures: initialSessionSignatures,
            usedSessionCount: form.usedSessionCount || 0
        });
    }

    extractSessionCountFromPackageDetails(packageDetails) {
        const match = packageDetails?.match(/(\d+)\s*sessions?/i);
        return match ? parseInt(match[1], 10) : 0;
    }

    componentDidMount() {
        const {patientId, form, readOnly} = this.props;
        const packageId = form.packageId;

        if (!readOnly) {
            this.populateFromEnhancedForm(form);
            return;
        }

        const patientPromise = new Promise((resolve, reject) => {
            RemotingService.getRemoteCall(
                `/api/patient/${patientId}`,
                null,
                (patient) => {
                    this.setState({
                        patientName: PatientUtil.getPatientName(patient.firstName, patient.middleName, patient.lastName),
                        mrnNo: StringUtil.mrn(patient.mrn),
                        mobileNo: patient.mobilePhone,
                        email: patient.email,
                    });
                    resolve();
                },
                reject
            );
        });


        const appointmentsPromise = new Promise((resolve, reject) => {
            RemotingService.getRemoteCall(
                `api/patients/${patientId}/packages/${packageId}/appointments`,
                null,
                (appointments) => {
                    const allAppointments = appointments || [];
                    this.setState({
                        completedAppointments: allAppointments
                    });
                    resolve();
                },
                reject
            );
        });

        const packagePromise = new Promise((resolve, reject) => {
            RemotingService.getRemoteCall(
                `api/patients/${patientId}/packages/${packageId}`,
                null,
                (packageData) => {
                    const sessionCount = packageData?.sessionCount || 0;
                    const usedSessionCount = packageData?.usedSessionCount || 0;

                    const existingSignatures = form?.sessionSignatures || [];
                    const initialSessionSignatures = Array(sessionCount).fill(null).map((_, index) => {
                        return existingSignatures[index] || {
                            sessionNumber: index + 1,
                            date: '',
                            therapist: '',
                            patientSignature: ''
                        };
                    });

                    this.setState({
                        packageDetails: `${EnumAppointmentType[packageData?.type]?.name || packageData?.type} - ${packageData?.sessionCount} sessions`,
                        endDate: moment(packageData?.validUntil).format('YYYY-MM-DD'),
                        sessionSignatures: initialSessionSignatures,
                        usedSessionCount: usedSessionCount
                    });
                    resolve();
                },
                reject
            );
        });

        Promise.all([patientPromise, appointmentsPromise, packagePromise])
            .then(() => {
                this.populateExistingSessionsWithAppointmentData();
            })
            .catch((error) => {
                console.error("Error in API calls - one or more calls failed:", error);
            });
    }

    onClose = () => {
        this.setState({
            language: undefined,

        });
        this.props.close();
    }

    onSubmit = () => {
        if (!ValidationUtil.checkWithNotification(this.formFields)) {
            return;
        }

        this.setState({language: undefined})

        if (this.props.readOnly) {
            this.props.close();

        } else {
            // Filter sessionSignatures to only include sessions that have meaningful data
            const completedSessionsCount = this.props.form?.sessionSignatures?.length || 0;
            const filteredSessionSignatures = this.state.sessionSignatures.filter((entry, index) => {
                // Include completed sessions (those that came from backend)
                if (index < completedSessionsCount) {
                    return true;
                }
                // For new sessions, only include if they have a signature or appointment selected
                return entry.patientSignature || entry.appointmentId;
            });

            const consentForm = {
                ...pick(this.state, ['consentDate', 'patientSignature', 'patientName', 'mrnNo', 'mobileNo', 'email', 'packageDetails', 'endDate']),
                sessionSignatures: filteredSessionSignatures
            };
            this.props.onFormComplete(consentForm);
        }
    }

    onSavePatientSignature = (signature) => {
        this.setState({patientSignature: signature})
    }

    onSaveSessionSignature = (index, signature) => {
        this.setState(prevState => {
            const newSessionSignatures = [...prevState.sessionSignatures];
            newSessionSignatures[index] = {...newSessionSignatures[index], patientSignature: signature};
            return {sessionSignatures: newSessionSignatures};
        });
    }

    handleSessionEntryChange = (index, field, value) => {
        this.setState(prevState => {
            const newSessionSignatures = [...prevState.sessionSignatures];
            newSessionSignatures[index] = {...newSessionSignatures[index], [field]: value};
            return {sessionSignatures: newSessionSignatures};
        });
    }

    handleAppointmentSelection = (index, appointmentId) => {
        const {completedAppointments} = this.state;

        this.setState(prevState => {
            const newSessionSignatures = [...prevState.sessionSignatures];

            if (appointmentId) {
                // Appointment selected - populate session data
                const selectedAppointment = completedAppointments.find(apt => apt.id === appointmentId);
                if (selectedAppointment) {
                    newSessionSignatures[index] = {
                        ...newSessionSignatures[index],
                        date: DateUtil.getTimeStringAtClinicZone(selectedAppointment.start, selectedAppointment.room?.clinic?.name, 'DD/MM/YYYY HH:mm'),
                        therapist: selectedAppointment.staff?.name || '',
                        appointmentId: appointmentId,
                        // Preserve existing sessionNumber, only set it if it doesn't exist
                        sessionNumber: newSessionSignatures[index]?.sessionNumber || index + 1,
                        patientSignature: newSessionSignatures[index]?.patientSignature || ''
                    };
                }
            } else {
                // Appointment deselected - clear session data but keep signature and sessionNumber
                newSessionSignatures[index] = {
                    ...newSessionSignatures[index],
                    date: '',
                    therapist: '',
                    appointmentId: null,
                    // Preserve existing sessionNumber, only set it if it doesn't exist
                    sessionNumber: newSessionSignatures[index]?.sessionNumber || index + 1,
                    // Keep the existing patient signature if it exists
                    patientSignature: newSessionSignatures[index]?.patientSignature || ''
                };
            }

            return {sessionSignatures: newSessionSignatures};
        });
    }

    populateExistingSessionsWithAppointmentData = () => {
        const {completedAppointments, sessionSignatures} = this.state;

        const populatedSessionSignatures = sessionSignatures.map((entry) => {
            if (entry.appointmentId) {
                const selectedAppointment = completedAppointments.find(apt => apt.id === entry.appointmentId);
                if (selectedAppointment) {
                    return {
                        ...entry,
                        date: DateUtil.getTimeStringAtClinicZone(selectedAppointment.start, selectedAppointment.room?.clinic?.name, 'DD/MM/YYYY HH:mm'),
                        therapist: selectedAppointment.staff?.name || '',
                    };
                }
            }
            return entry;
        });
        this.setState({sessionSignatures: populatedSessionSignatures});
    }

    render() {
        if (!this.props.show) {
            return null;
        }

        let form = this.props.form ? this.props.form : pick(this.state, ['consentDate', 'patientSignature', 'patientName', 'mrnNo', 'mobileNo', 'email', 'packageDetails', 'endDate', 'sessionSignatures']);
        let {readOnly} = this.props;
        this.formFields = [];

        const inEnglish = this.props.language === 'English';
        let installationSpecificConfig = null;

        switch (DEFAULT_INSTALLATION) {
            case EnumInstallation.KWT:
                installationSpecificConfig = {
                    disclaimer_en: 'This package cannot be used in UAE branches.',
                    disclaimer_ar: 'لا يمكن استخدام هذه الباقة في فروع الإمارات.',
                };
                break;
            case EnumInstallation.RAD:
            case EnumInstallation.DXB:
                installationSpecificConfig = {
                    disclaimer_en: 'This package cannot be used in Abu Dhabi branch.',
                    disclaimer_ar: 'لا يمكن استخدام هذه الباقة في فرع أبوظبي.',
                };
                break;
            case EnumInstallation.ADH:
                installationSpecificConfig = {
                    disclaimer_en: 'This package cannot be used in Dubai branch.',
                    disclaimer_ar: 'لا يمكن استخدام هذه الباقة في فرع دبي.',
                };
                break;
            default:
                console.log("Error: Unknown installation type");
        }

        return (
            <Wizard
                contentStyle={{width: 1150}}
                fullScreen={this.props.fullScreen}
                show={this.props.show}
                close={this.onClose}
                title={inEnglish ? "MEMBERSHIP PACKAGE" : "باقة العضوية"}
                onSubmit={this.onSubmit}
                isReadOnly={readOnly}
            >
                <Wizard.Page>
                    <div style={{maxWidth: '950px'}}>
                        <div style={{
                            marginTop: '15px', marginBottom: '15px', width: '100%', display: 'flex',
                            flexFlow: 'row', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            {<ClinicLogo/>}
                            <div>
                                <h2>{inEnglish ? "MEMBERSHIP PACKAGE" : "باقة العضوية"}</h2>
                            </div>
                        </div>

                        <hr style={{
                            borderTop: "2px solid #000000",
                            marginTop: "-15px",
                            boxShadow: "0px 2px 2px #797979"
                        }}/>

                        <div className={"container"} style={inEnglish ? {marginTop: "20px"} : {
                            marginTop: "20px",
                            direction: "rtl",
                            textAlign: "right"
                        }}>
                            <div className={"row"} style={{marginBottom: "10px"}}>
                                <div
                                    className="col-md-3 dim14-form-label">{inEnglish ? 'Member Name' : 'اسم العضو'}</div>
                                <div className="col-md-9"><TextInput disabled={true} style={{width: '100%'}}
                                                                     value={this.state.patientName}/></div>
                            </div>
                            <div className={"row"} style={{marginBottom: "10px"}}>
                                <div
                                    className="col-md-3 dim14-form-label">{inEnglish ? 'MRN No' : 'رقم السجل الطبي'}</div>
                                <div className="col-md-9"><TextInput disabled={true} style={{width: '100%'}}
                                                                     value={this.state.mrnNo}/></div>
                            </div>
                            <div className={"row"} style={{marginBottom: "10px"}}>
                                <div
                                    className="col-md-3 dim14-form-label">{inEnglish ? 'Mobile No' : 'رقم الجوال'}</div>
                                <div className="col-md-9"><TextInput disabled={true} style={{width: '100%'}}
                                                                     value={this.state.mobileNo}/></div>
                            </div>
                            <div className={"row"} style={{marginBottom: "10px"}}>
                                <div
                                    className="col-md-3 dim14-form-label">{inEnglish ? 'Email' : 'البريد الإلكتروني'}</div>
                                <div className="col-md-9"><TextInput disabled={true} style={{width: '100%'}}
                                                                     value={this.state.email}/></div>
                            </div>
                            <div className={"row"} style={{marginBottom: "10px"}}>
                                <div
                                    className="col-md-3 dim14-form-label">{inEnglish ? 'Package Details (No of sessions)' : 'تفاصيل الباقة (عدد الجلسات)'}</div>
                                <div className="col-md-9"><TextInput disabled={true} style={{width: '100%'}}
                                                                     value={this.state.packageDetails}/></div>
                            </div>
                            <div className={"row"} style={{marginBottom: "10px"}}>
                                <div
                                    className="col-md-3 dim14-form-label">{inEnglish ? 'Start Date' : 'تاريخ البدء'}</div>
                                <div className="col-md-9"><TextInput disabled={true} style={{width: '100%'}}
                                                                     value={form.consentDate ? moment(form.consentDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')}/>
                                </div>
                            </div>
                            <div className={"row"} style={{marginBottom: "20px"}}>
                                <div
                                    className="col-md-3 dim14-form-label">{inEnglish ? 'End Date' : 'تاريخ الانتهاء'}</div>
                                <div className="col-md-9"><TextInput disabled={true} style={{width: '100%'}}
                                                                     value={this.state.endDate}/></div>
                            </div>
                            <table className="table table-bordered" style={{width: '110%', marginBottom: '20px'}}>
                                <thead>
                                <tr>
                                    <th style={{
                                        width: '35%',
                                        textAlign: inEnglish ? 'left' : 'right'
                                    }}>{inEnglish ? 'DATE' : 'التاريخ'}</th>
                                    <th style={{
                                        width: '40%',
                                        textAlign: inEnglish ? 'left' : 'right'
                                    }}>{inEnglish ? 'PROVIDER' : 'المعالج'}</th>
                                    <th style={{
                                        width: '25%',
                                        textAlign: inEnglish ? 'left' : 'right'
                                    }}>{inEnglish ? 'SIGNATURE' : 'التوقيع'}</th>
                                    <th style={{
                                        width: '15%',
                                        textAlign: inEnglish ? 'left' : 'right'
                                    }}>{inEnglish ? 'SESSION' : 'الجلسة'}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {this.state.sessionSignatures.map((entry, index) => {
                                    const completedSessionsCount = this.props.form?.sessionSignatures?.length || 0;

                                    const isCompletedSession = index < completedSessionsCount;
                                    const isFormNew = !this.props.form?.patientSignature;
                                    const isDisabled = readOnly || isFormNew;

                                    return (
                                        <tr key={index}>
                                            <td>
                                                {!readOnly && !isFormNew ? (
                                                    <div>
                                                        <SelectInput
                                                            style={{
                                                                width: '100%'
                                                            }}
                                                            placeholder="Select appointment..."
                                                            value={entry.appointmentId || ''}
                                                            options={(() => {
                                                                const usedAppointmentIds = this.state.sessionSignatures
                                                                    .filter(session => session.appointmentId)
                                                                    .map(session => session.appointmentId);

                                                                return this.state.completedAppointments
                                                                    .filter(appointment =>
                                                                        !usedAppointmentIds.includes(appointment.id) ||
                                                                        appointment.id === entry.appointmentId
                                                                    )
                                                                    .map(appointment => ({
                                                                        label: `${DateUtil.getTimeStringAtClinicZone(appointment.start, appointment.room?.clinic?.name, 'DD/MM/YYYY HH:mm')} - ${appointment.staff?.name || 'No staff'}`,
                                                                        value: appointment.id
                                                                    }));
                                                            })()}
                                                            displayProperty="label"
                                                            valueProperty="value"
                                                            onChange={(value) => this.handleAppointmentSelection(index, value)}
                                                            showClear={true}
                                                            searchEnabled={true}
                                                        />
                                                    </div>
                                                ) : (
                                                    <TextInput
                                                        disabled={isDisabled}
                                                        style={{
                                                            width: '100%',
                                                            border: 'none',
                                                            textAlign: 'center',
                                                            backgroundColor: isFormNew ? '#f5f5f5' : 'transparent'
                                                        }}
                                                        value={entry.date}
                                                        onChange={(e) => this.handleSessionEntryChange(index, 'date', e.target.value)}
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                <TextInput
                                                    disabled={isDisabled}
                                                    style={{
                                                        width: '100%',
                                                        border: 'none',
                                                        textAlign: 'center',
                                                        backgroundColor: isFormNew ? '#f5f5f5' : 'transparent'
                                                    }}
                                                    value={entry.therapist}
                                                    onChange={(e) => this.handleSessionEntryChange(index, 'therapist', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <SignatureInput
                                                    signature={entry.patientSignature}
                                                    onSave={(signature) => this.onSaveSessionSignature(index, signature)}
                                                    readOnly={isDisabled}
                                                    showClearButton={!isCompletedSession}
                                                    style={{
                                                        backgroundColor: isFormNew ? '#f5f5f5' : 'transparent'
                                                    }}/>
                                            </td>
                                            <td style={{
                                                textAlign: 'center',
                                                backgroundColor: isFormNew ? '#f5f5f5' : 'transparent'
                                            }}>
                                                {index + 1}
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>

                            <div style={inEnglish ? {marginBottom: '20px'} : {
                                marginBottom: '20px',
                                direction: "rtl",
                                textAlign: "right"
                            }}>
                                <h4>{inEnglish ? 'MEMBERSHIP TERMS & CONDITIONS:' : 'أحكام وشروط العضوية:'}</h4>
                                <ul>
                                    <li>
                                        {inEnglish ? installationSpecificConfig.disclaimer_en : installationSpecificConfig.disclamer_ar}
                                    </li>
                                    <li>{inEnglish ? 'Sessions are non-transferrable and non-refundable.' : 'الجلسات غير قابلة للتحويل وغير قابلة للاسترداد.'}</li>
                                    <li>{inEnglish ? 'This package does not include services with our Medical Doctors' : 'هذه الباقة لا تشمل خدمات الأطباء لدينا'}</li>
                                    <li>{inEnglish ? 'Reimbursement of sessions under this package may not be available through insurance.' : 'قد لا يكون تعويض الجلسات ضمن هذه الباقة متاحًا من خلال التأمين.'}</li>
                                    <li>{inEnglish ? 'On the expiry date given (above); will render this package completed. Strictly no extension.' : 'في تاريخ انتهاء الصلاحية المحدد (أعلاه)؛ ستعتبر هذه الباقة مكتملة. لا يوجد تمديد على الإطلاق.'}</li>
                                    <li>{inEnglish ? 'You may cancel your appointment without charge if you cancel with at least 6 hours\' notice prior to your appointment.' : 'يمكنك إلغاء موعدك بدون رسوم إذا قمت بالإلغاء قبل 6 ساعات على الأقل من موعدك.'}</li>
                                    <li>{inEnglish ? 'Late Cancellations/No shows without prior notice will result in a full charge of session being deducted from your package.' : 'الإلغاء المتأخر / عدم الحضور بدون إشعار مسبق سيؤدي إلى خصم قيمة الجلسة كاملة من باقتك.'}</li>
                                </ul>
                            </div>

                            <div className={"row"} style={inEnglish ? {marginTop: "20px", marginBottom: "20px"} : {
                                marginTop: "20px",
                                marginBottom: "20px",
                                direction: "rtl",
                                textAlign: "right"
                            }}>
                                <div className="col-md-12">
                                    {inEnglish ? 'I UNDERSTAND AND AGREE TO THE TERMS AND CONDITIONS OUTLINED ABOVE' : 'أفهم وأوافق على الشروط والأحكام الموضحة أعلاه'}
                                </div>
                            </div>

                            <div className="row" style={inEnglish ? {marginTop: "15px"} : {
                                marginTop: "15px",
                                direction: "rtl",
                                textAlign: "right"
                            }}>
                                <div className="col-md-6 dim14-form-label">
                                    {inEnglish ? 'PATIENT\'S SIGNATURE' : 'توقيع المريض'}
                                </div>
                                <div className="col-md-3 dim14-form-label">
                                    {inEnglish ? 'DATE' : 'التاريخ'}
                                </div>
                            </div>

                            <div className="row" style={inEnglish ? {} : {direction: "rtl", textAlign: "right"}}>
                                <div className="col-md-6">
                                    <FormField ref={formField => this.formFields.push(formField)}
                                               disabled={readOnly || form.patientSignature}
                                               required requiredMessage="Signature required"
                                               validateOn={this.state.patientSignature}>
                                        <SignatureInput
                                            signature={readOnly ? form.patientSignature : this.state.patientSignature}
                                            onSave={this.onSavePatientSignature}
                                            readOnly={readOnly || form.patientSignature}/>
                                    </FormField>
                                </div>
                                <div className="col-md-3 dim14-base-input"
                                     style={{textAlign: inEnglish ? 'left' : 'right'}}>
                                    {moment(form.consentDate || this.state.consentDate).format('DD/MM/YY')}
                                </div>
                            </div>
                        </div>
                    </div>
                </Wizard.Page>
            </Wizard>
        );
    }
}

export default PackageConsentForm;
